import React, { useState } from 'react';
import { IconCheck, IconX, IconPlus, IconShield } from '../components/Icons';

/**
 * risklo.io/companies: plans for companies that run RiskLo for their own clients
 * (strategy providers, signal services, prop communities). Self users see
 * /pricing. Plans match Admin → Brands → Plan (server/utils/entityPlans.js).
 */

const COMPANY_PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    price: '$149',
    tagline: 'Put your strategies and clients on RiskLo, ready in a day.',
    perks: [
      'Your company workspace on RiskLo, with your logo and color',
      'Your strategies on the Overview, Results and Portfolio Simulator',
      'Trade Watcher: live NinjaTrader accounts and naked-position alerts',
      '50 active clients included, then $3 per client',
      'Email support',
    ],
  },
  {
    id: 'brand',
    name: 'Brand',
    price: '$499',
    featured: true,
    tagline: 'Your look, your sign-in page, your workflow. On RiskLo.',
    perks: [
      'Everything in Starter',
      'Your full color theme and a layout style (Classic, Clean or Sharp)',
      'Your own sign-in link that opens on your brand',
      'Alert emails from “Your Company via RiskLo”, in your colors',
      'CSAs, hands-off client monitoring and the alert-only add-on',
      '250 active clients included, then $2 per client',
      'Priority support',
    ],
  },
  {
    id: 'whitelabel',
    name: 'White-label',
    price: '$1,500',
    tagline: 'Your product, end to end. RiskLo runs underneath.',
    perks: [
      'Everything in Brand',
      'Your app name, tagline and browser-tab icon everywhere',
      'Your own domain (app.yourcompany.com)',
      'Alert emails from your name, and the NinjaTrader add-on window in your name',
      'RiskLo shows only as a small “Powered by RiskLo”',
      '1,000 active clients included, then $1 per client',
      'Dedicated support and an onboarding call',
      'One-time setup from $1,000',
    ],
  },
];

// [feature, Starter, Brand, White-label]: true / false, or a short text.
const COMPARE_ROWS = [
  ['RiskLo name in the app', 'Yes', 'Yes, with your brand', 'Only “Powered by RiskLo”'],
  ['Your logo', true, true, true],
  ['Full color theme', false, true, true],
  ['Layout style (Classic, Clean, Sharp)', false, true, true],
  ['Your sign-in link', true, true, true],
  ['Your own domain', false, false, true],
  ['Your app name and tab icon', false, false, true],
  ['Alert emails', 'From RiskLo', '“You via RiskLo”', 'From you'],
  ['Trade Watcher + naked alerts', true, true, true],
  ['CSAs and hands-off monitoring', false, true, true],
  ['Add-on window in your name', false, false, true],
  ['Active clients included', '50', '250', '1,000'],
  ['Each extra client', '$3', '$2', '$1'],
  ['Support', 'Email', 'Priority', 'Dedicated'],
];

const COMPANY_FAQ = [
  {
    q: 'What is an active client?',
    a: 'Someone who signed in or had an account report to Trade Watcher during the month. Clients who never log in don’t count.',
  },
  {
    q: 'Is there a discount for paying yearly?',
    a: 'Yes: pay yearly and get two months free on any plan.',
  },
  {
    q: 'Can we change plans later?',
    a: 'Any time. Moving up unlocks the new branding straight away; your strategies, clients and settings stay as they are.',
  },
  {
    q: 'Will our clients see other companies on RiskLo?',
    a: 'No. Each company has its own workspace, strategies and clients, and your clients only ever see yours. On White-label they don’t see RiskLo either, apart from a small “Powered by RiskLo”.',
  },
  {
    q: 'Trading for yourself?',
    a: 'Individual traders don’t need a company plan. See Pricing for Basic (free) and Pro.',
  },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`marketing-faq-item ${open ? 'is-open' : ''}`}>
      <button type="button" className="marketing-faq-q" onClick={() => setOpen((v) => !v)}>
        <span>{q}</span>
        <IconPlus size={18} />
      </button>
      <div className="marketing-faq-a">
        <p>{a}</p>
      </div>
    </div>
  );
}

function Cell({ value }) {
  if (value === true) return <IconCheck size={18} color="currentColor" />;
  if (value === false) return <IconX size={18} color="currentColor" />;
  return <span className="marketing-compare-text">{value}</span>;
}

function CompaniesPage({ onNavigate }) {
  const go = (page) => () => onNavigate && onNavigate(page);

  return (
    <>
      <section className="marketing-page-header">
        <div className="marketing-container marketing-container-narrow">
          <div className="marketing-eyebrow marketing-reveal">For companies</div>
          <h1 className="marketing-title marketing-reveal" data-delay="1">
            Run your strategy business <span className="accent">on RiskLo<sup className="reg-mark">®</sup>.</span>
          </h1>
          <p className="marketing-subtitle marketing-reveal" data-delay="2" style={{ margin: '1rem auto 0' }}>
            Give your clients results dashboards, a portfolio simulator and live NinjaTrader monitoring under your
            brand. Start on RiskLo’s template and grow into your own look, or your own product.
          </p>
        </div>
      </section>

      <section className="marketing-section" style={{ paddingTop: 0 }}>
        <div className="marketing-container">
          <div className="marketing-pricing-grid">
            {COMPANY_PLANS.map((p, i) => (
              <div
                key={p.id}
                className={`marketing-plan ${p.featured ? 'featured' : ''} marketing-reveal`}
                data-delay={i + 1}
              >
                {p.featured && <span className="marketing-plan-badge">Most popular</span>}
                <span className="marketing-plan-name">{p.name}</span>
                <div className="marketing-plan-price">
                  {p.price}
                  <small>/month</small>
                </div>
                <p className="marketing-plan-tagline">{p.tagline}</p>
                <ul className="marketing-plan-list">
                  {p.perks.map((perk) => (
                    <li key={perk}><IconCheck size={16} /> {perk}</li>
                  ))}
                </ul>
                <button
                  type="button"
                  className={`${p.featured ? 'marketing-btn' : 'marketing-btn-ghost'} marketing-plan-cta`}
                  onClick={go('contact')}
                >
                  Talk to us
                </button>
              </div>
            ))}
          </div>
          <p className="marketing-subtitle" style={{ textAlign: 'center', margin: '1.25rem auto 0', fontSize: '0.95rem' }}>
            Billed monthly. Pay yearly and get two months free.
          </p>
        </div>
      </section>

      <section className="marketing-section tight">
        <div className="marketing-container">
          <div className="marketing-section-heading marketing-reveal">
            <div className="marketing-eyebrow">Compare plans</div>
            <h2 className="marketing-title" style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)' }}>
              How much of it is yours.
            </h2>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="marketing-compare-table marketing-reveal">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th className="center">Starter</th>
                  <th className="center">Brand</th>
                  <th className="center">White-label</th>
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map(([feature, ...values]) => (
                  <tr key={feature}>
                    <td className="feature">{feature}</td>
                    {values.map((v, idx) => (
                      <td key={idx} className="center"><Cell value={v} /></td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="marketing-section tight">
        <div className="marketing-container">
          <div className="marketing-section-heading marketing-reveal">
            <div className="marketing-eyebrow"><IconShield size={14} /> Questions</div>
            <h2 className="marketing-title" style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)' }}>
              Company plans, answered.
            </h2>
          </div>
          <div className="marketing-faq">
            {COMPANY_FAQ.map((item) => <FaqItem key={item.q} q={item.q} a={item.a} />)}
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginTop: '2rem', flexWrap: 'wrap' }}>
            <button type="button" className="marketing-btn marketing-btn-lg" onClick={go('contact')}>Talk to us</button>
            <button type="button" className="marketing-btn-ghost marketing-btn-lg" onClick={go('pricing')}>Plans for traders</button>
          </div>
        </div>
      </section>
    </>
  );
}

export default CompaniesPage;
